import { CSSProperties } from 'react';
import { Image } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { Corners, FrameAlignment } from '../../../types';
import { ImageWithDimensions } from './types';

export const EMPTY_IMAGE_SIZE = 250;

export const calculateHorizontalJustifiedLayout = (
  images: ImageWithDimensions[],
  rowHeight: number,
  imageSpacing = 0
) => {
  // TODO: Handle instances where image.width and image.height are 0. Maybe have a default height/width?
  const aspectRatios = images.map((image) => image.width / image.height);
  const scaledWidths = aspectRatios.map((ratio) => ratio * rowHeight);
  const spacingWidth = (images.length - 1) * imageSpacing;
  const totalWidth = scaledWidths.reduce((totalWidth, width) => totalWidth + width, 0) + spacingWidth;
  const percentageWidths = scaledWidths.map((width) => (width / totalWidth) * 100 + '%');
  const percentageSpacing = (imageSpacing / totalWidth) * 100;
  const maxTableWidth = totalWidth + 'px';

  return { percentageWidths, percentageSpacing, maxTableWidth };
};

export const getJustifiedLayoutPaddingStyle = (
  index: number,
  imageCount: number,
  imageSpacing = 0,
  unit = '%',
  isVertical = false
): CSSProperties => {
  const halvedSpacing = imageSpacing / 2 + unit;

  switch (index) {
    case 0:
      if (imageCount === 1) return {};
      return isVertical ? { paddingBottom: halvedSpacing } : { paddingRight: halvedSpacing };

    case imageCount - 1:
      return isVertical ? { paddingTop: halvedSpacing } : { paddingLeft: halvedSpacing };

    default:
      return isVertical
        ? { paddingTop: halvedSpacing, paddingBottom: halvedSpacing }
        : { paddingLeft: halvedSpacing, paddingRight: halvedSpacing };
  }
};

export const getCorners = (corners?: string): Corners => {
  const validCorners = ['square', 'rounded', 'pill'];
  return corners && validCorners.includes(corners) ? (corners as Corners) : 'square';
};

export const getAlignment = (alignment?: string): FrameAlignment => {
  const validAlignments = ['center', 'left', 'right'];
  return alignment && validAlignments.includes(alignment) ? (alignment as FrameAlignment) : 'center';
};

export const getPadding = (
  paddingTop?: string,
  paddingRight?: string,
  paddingBottom?: string,
  paddingLeft?: string
): CSSProperties => {
  return {
    paddingTop: getNonNegativeNumber(paddingTop),
    paddingRight: getNonNegativeNumber(paddingRight),
    paddingLeft: getNonNegativeNumber(paddingLeft),
    paddingBottom: getNonNegativeNumber(paddingBottom),
  };
};

export const getNonNegativeNumber = (input?: string | number) => {
  if (input === undefined || input === null) return undefined;

  // Handle numbers
  if (typeof input === 'number') {
    return input >= 0 ? input : undefined;
  }

  // Handle strings
  const sanitizedInput = input.replace(/[^\d.-]/g, '').trim();
  const parsedInput = parseFloat(sanitizedInput);
  return !isNaN(parsedInput) && parsedInput >= 0 ? parsedInput : undefined;
};

export const newImage: Image = { name: '', url: '' };

export const newImageWithDimensions = (image?: Image | ImageWithDimensions): ImageWithDimensions => {
  if (image === undefined) {
    return {
      name: '',
      url: '',
      crop: undefined,
      width: EMPTY_IMAGE_SIZE,
      height: EMPTY_IMAGE_SIZE,
    };
  }

  if (isImageWithDimensions(image)) return { ...image };

  return addDimensions(image);
};

/**
 * Type guard to check if an object is an ImageWithDimensions
 * @param obj The object to check
 * @returns boolean indicating if the object is an ImageWithDimensions
 */
const isImageWithDimensions = (obj: any): obj is ImageWithDimensions => {
  // Is it an object?
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  // Is it an Image?
  if (typeof obj.name !== 'string' || typeof obj.url !== 'string') {
    return false;
  }

  // Is it an ImageWithDimensions?
  return 'width' in obj && 'height' in obj;
};

export const createImagesWithDimensions = (imageCount: number, images: Image[] | ImageWithDimensions[] = []) =>
  Array.from({ length: imageCount }).map((_, index) => newImageWithDimensions(images[index]));

/**
 * Converts an Image to an ImageWithDimensions by adding width and height
 */
export const addDimensions = (image: Image, width = 0, height = 0): ImageWithDimensions => {
  return {
    ...image,
    width,
    height,
  };
};

/**
 * Converts an ImageWithDimensions to an Image by removing width and height
 */
export const removeDimensions = (imageWithDimensions: ImageWithDimensions): Image => {
  const { width, height, ...image } = imageWithDimensions;
  return image;
};
